import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Dialog, Transition } from '@headlessui/react'
import {
  ArrowDownIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  UserIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowPathIcon,
  BanknotesIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline'
import { walletApi, TransactionCategory } from '@/api/walletApi'
import { apiClient } from '@/api/client'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import toast from 'react-hot-toast'

const debitSchema = z.object({
  userId: z.string().min(1, 'Please select a user'),
  amount: z.number().min(1, 'Amount must be at least 1'),
  category: z.string().min(1, 'Please select a category'),
  description: z.string().min(1, 'Description is required'),
  notes: z.string().optional(),
})

type DebitFormData = z.infer<typeof debitSchema>

const categories: { value: TransactionCategory; label: string }[] = [
  { value: 'SCHOOL_FEES', label: 'School Fees' },
  { value: 'TRANSPORT_FEES', label: 'Transport Fees' },
  { value: 'LIBRARY_FEES', label: 'Library Fees' },
  { value: 'EXAM_FEES', label: 'Exam Fees' },
  { value: 'UNIFORM', label: 'Uniform' },
  { value: 'BOOKS', label: 'Books' },
  { value: 'MEALS', label: 'Meals' },
  { value: 'ACTIVITIES', label: 'Activities' },
  { value: 'PENALTY', label: 'Penalty' },
  { value: 'OTHER', label: 'Other' },
]

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function DebitWalletPage() {
  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [showSuccess, setShowSuccess] = useState(false)
  const [lastTransaction, setLastTransaction] = useState<any>(null)
  const [filterCategory, setFilterCategory] = useState<string>('')

  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm<DebitFormData>({
    resolver: zodResolver(debitSchema),
    defaultValues: {
      category: 'SCHOOL_FEES',
    },
  })

  const amount = watch('amount')

  // Fetch debit transactions (type: DEBIT)
  const { data: debitsData, isLoading: debitsLoading, refetch } = useQuery({
    queryKey: ['debitTransactions', filterCategory],
    queryFn: async () => {
      const params: Record<string, string> = { type: 'DEBIT', limit: '50' }
      if (filterCategory) params.category = filterCategory
      const response = await apiClient.get('/wallet/transactions', { params })
      return response.data
    },
  })

  // Search users for modal
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ['searchUsers', searchQuery],
    queryFn: async () => {
      if (searchQuery.length < 2) return { data: { users: [] } }
      const response = await apiClient.get('/users', {
        params: { search: searchQuery, limit: 10 },
      })
      return response.data
    },
    enabled: searchQuery.length >= 2,
  })

  // Get selected user's wallet
  const { data: walletData } = useQuery({
    queryKey: ['userWallet', selectedUser?._id],
    queryFn: () => walletApi.getUserWallet(selectedUser._id),
    enabled: !!selectedUser?._id,
  })

  const debitMutation = useMutation({
    mutationFn: walletApi.debitWallet,
    onSuccess: (data) => {
      toast.success('Wallet debited successfully!')
      setLastTransaction(data.data)
      setShowSuccess(true)
      queryClient.invalidateQueries({ queryKey: ['userWallet'] })
      queryClient.invalidateQueries({ queryKey: ['debitTransactions'] })
      queryClient.invalidateQueries({ queryKey: ['allTransactions'] })
      queryClient.invalidateQueries({ queryKey: ['allWallets'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to debit wallet')
    },
  })

  const handleSelectUser = (user: any) => {
    setSelectedUser(user)
    setValue('userId', user._id)
    setSearchQuery('')
  }

  const onSubmit = (data: DebitFormData) => {
    if (!data.userId || !data.amount) {
      toast.error('User and amount are required')
      return
    }
    debitMutation.mutate({
      userId: data.userId,
      amount: data.amount,
      category: data.category as TransactionCategory,
      description: data.description,
      notes: data.notes,
    })
  }

  const handleOpenModal = () => {
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    reset()
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    reset()
  }

  const handleNewTransaction = () => {
    setShowSuccess(false)
    setSelectedUser(null)
    setLastTransaction(null)
    setSearchQuery('')
    reset({
      userId: '',
      amount: undefined,
      category: 'SCHOOL_FEES',
      description: '',
      notes: '',
    })
  }

  const currentBalance = walletData?.data?.balance || 0
  const insufficientBalance = amount > 0 && amount > currentBalance

  const debits = debitsData?.data?.transactions || []

  // Calculate totals
  const totalDebits = debits.reduce((sum: number, t: any) => sum + t.amount, 0)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Debit Transactions</h1>
          <p className="text-gray-600">View all wallet debits and deduct funds</p>
        </div>
        <button
          onClick={handleOpenModal}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
        >
          <PlusIcon className="w-5 h-5" />
          Debit Wallet
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Debits</p>
              <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDebits)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <ArrowDownIcon className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Transactions</p>
              <p className="text-2xl font-bold text-gray-900">{debits.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <BanknotesIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Average Debit</p>
              <p className="text-2xl font-bold text-gray-900">
                {debits.length > 0 ? formatCurrency(totalDebits / debits.length) : 'KES 0'}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CalendarDaysIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowPathIcon className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Debits List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {debitsLoading ? (
          <div className="p-8 text-center">
            <LoadingSpinner size="lg" />
          </div>
        ) : debits.length === 0 ? (
          <div className="p-8 text-center">
            <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No debit transactions found</p>
            <button
              onClick={handleOpenModal}
              className="mt-4 px-4 py-2 text-red-600 hover:text-red-700 font-medium"
            >
              Make a debit
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reference
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {debits.map((transaction: any) => (
                  <tr key={transaction._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <UserIcon className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {transaction.userId?.firstName} {transaction.userId?.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{transaction.userId?.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-red-600 font-semibold">
                        -{formatCurrency(transaction.amount)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                        {transaction.category?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 max-w-xs">
                      <p className="text-sm text-gray-600 truncate">{transaction.description || '-'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-xs font-mono text-gray-500">{transaction.reference}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(transaction.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Debit Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleCloseModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                  {showSuccess && lastTransaction ? (
                    <div className="p-6 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <CheckCircleIcon className="w-10 h-10 text-green-600" />
                      </div>
                      <Dialog.Title className="text-2xl font-bold text-gray-900 mb-2">
                        Debit Successful!
                      </Dialog.Title>
                      <p className="text-gray-600 mb-6">The wallet has been debited successfully.</p>

                      <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left space-y-3">
                        <div className="flex justify-between">
                          <span className="text-gray-500">Reference</span>
                          <span className="font-mono text-sm">{lastTransaction.transaction.reference}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">Amount Debited</span>
                          <span className="font-semibold text-red-600">
                            -{formatCurrency(lastTransaction.transaction.amount)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">New Balance</span>
                          <span className="font-semibold">{formatCurrency(lastTransaction.wallet.balance)}</span>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button
                          onClick={handleNewTransaction}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                        >
                          New Debit
                        </button>
                        <button
                          onClick={handleCloseModal}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center justify-between p-6 border-b border-gray-200">
                        <Dialog.Title className="text-lg font-semibold text-gray-900">
                          Debit Wallet
                        </Dialog.Title>
                        <button
                          onClick={handleCloseModal}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <XMarkIcon className="w-6 h-6" />
                        </button>
                      </div>

                      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                        {/* User Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Select User *</label>
                          {selectedUser ? (
                            <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                  <UserIcon className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {selectedUser.firstName} {selectedUser.lastName}
                                  </p>
                                  <p className="text-xs text-gray-500">{selectedUser.email}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                {walletData?.data && (
                                  <p className="text-xs text-gray-500">
                                    Balance: <span className={`font-semibold ${currentBalance > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                      {formatCurrency(currentBalance)}
                                    </span>
                                  </p>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedUser(null)
                                    setValue('userId', '')
                                  }}
                                  className="text-xs text-red-600 hover:text-red-700"
                                >
                                  Change
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="relative">
                              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                              <input
                                type="text"
                                placeholder="Search by name, email, or phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />

                              {searchQuery.length >= 2 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                                  {searchLoading ? (
                                    <div className="p-3 text-center">
                                      <LoadingSpinner size="sm" />
                                    </div>
                                  ) : searchResults?.data?.users?.length > 0 ? (
                                    searchResults.data.users.map((user: any) => (
                                      <button
                                        key={user._id}
                                        type="button"
                                        onClick={() => handleSelectUser(user)}
                                        className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 text-left"
                                      >
                                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                                          <UserIcon className="w-4 h-4 text-gray-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="font-medium text-gray-900 text-sm truncate">
                                            {user.firstName} {user.lastName}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                                        </div>
                                        <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                          {user.role}
                                        </span>
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-3 text-center text-sm text-gray-500">No users found</div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          {errors.userId && (
                            <p className="mt-1 text-sm text-red-600">{errors.userId.message}</p>
                          )}
                        </div>

                        {/* Amount */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Amount (KES) *</label>
                          <input
                            type="number"
                            {...register('amount', { valueAsNumber: true })}
                            placeholder="Enter amount"
                            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                              insufficientBalance ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                          />
                          {errors.amount && (
                            <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
                          )}
                        </div>

                        {insufficientBalance && (
                          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
                            <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0" />
                            <p className="text-sm">
                              Insufficient balance. Available: {formatCurrency(currentBalance)}
                            </p>
                          </div>
                        )}

                        {/* Category */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                          <select
                            {...register('category')}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          >
                            <option value="">Select category</option>
                            {categories.map((cat) => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                          {errors.category && (
                            <p className="mt-1 text-sm text-red-600">{errors.category.message}</p>
                          )}
                        </div>

                        {/* Description */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                          <input
                            type="text"
                            {...register('description')}
                            placeholder="e.g., Term 1 School Fees Payment"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                          />
                          {errors.description && (
                            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                          )}
                        </div>

                        {/* Summary */}
                        {amount > 0 && selectedUser && (
                          <div className={`rounded-lg p-3 ${insufficientBalance ? 'bg-red-50 border border-red-200' : 'bg-red-50 border border-red-200'}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-700">Amount to Debit</span>
                              <span className="text-xl font-bold text-red-600">-{formatCurrency(amount)}</span>
                            </div>
                            {walletData?.data && (
                              <div className="flex items-center justify-between text-sm text-gray-600 mt-1">
                                <span>New Balance (Est.)</span>
                                <span className={`font-semibold ${currentBalance - (amount || 0) < 0 ? 'text-red-600' : ''}`}>
                                  {formatCurrency(Math.max(0, currentBalance - (amount || 0)))}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Submit Button */}
                        <button
                          type="submit"
                          disabled={debitMutation.isPending || !selectedUser || insufficientBalance}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                        >
                          {debitMutation.isPending ? (
                            <>
                              <LoadingSpinner size="sm" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <ArrowDownIcon className="w-5 h-5" />
                              Debit Wallet
                            </>
                          )}
                        </button>
                      </form>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
